import type { ReactNode } from 'react';
import { useState } from 'react';

import { Button, CircularProgress, LinearProgress, Typography } from '@mui/material';
import { CheckIcon } from '@truvhq/ui';
import { clsx } from 'clsx';

import { useChecklist } from 'entities/onboarding/api/useChecklist';
import type { ChecklistStep } from 'entities/onboarding/types';
import type { OnStepSettled, SettledStatus, SettledStepInfo } from 'features/onboarding/lib/conversation';
import { toYouTubeEmbedUrl } from 'features/onboarding/lib/video';
import { TruvAvatar } from 'features/onboarding/ui/OnboardingMessages';

function StatusIcon({ status }: { status: ChecklistStep['status'] }) {
  if (status === 'complete') {
    return (
      <div
        role="img"
        aria-label="Completed"
        className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-success text-white"
      >
        <CheckIcon width={12} height={12} aria-hidden />
      </div>
    );
  }
  return (
    <div
      role="img"
      aria-label={status === 'skipped' ? 'Skipped' : 'Not done'}
      className={clsx(
        'mt-0.5 h-5 w-5 shrink-0 rounded-full border-2',
        status === 'skipped' ? 'border-grey30 bg-grey20' : 'border-grey40',
      )}
    />
  );
}

// Shared "Open" link + collapsible video for a step. `extraActions` lets a caller add buttons
// (e.g. mark done / skip) into the same action row.
function StepMedia({
  title,
  link,
  videoUrl,
  extraActions,
}: {
  title: string;
  link?: string | null;
  videoUrl?: string | null;
  extraActions?: ReactNode;
}) {
  const [showVideo, setShowVideo] = useState(false);
  const embedUrl = toYouTubeEmbedUrl(videoUrl);
  const hasActions = Boolean(link || embedUrl || extraActions);
  return (
    <>
      {hasActions && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {link && (
            <Button
              component="a"
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.75rem', py: 0.25 }}
            >
              Open
            </Button>
          )}
          {embedUrl && (
            <Button
              size="small"
              variant="text"
              sx={{ fontSize: '0.75rem' }}
              onClick={() => setShowVideo((shown) => !shown)}
              aria-expanded={showVideo}
            >
              {showVideo ? 'Hide video' : 'Watch video'}
            </Button>
          )}
          {extraActions}
        </div>
      )}
      {embedUrl && showVideo && (
        <div className="relative mt-2 w-full overflow-hidden rounded-lg" style={{ paddingBottom: '56.25%' }}>
          <iframe
            className="absolute inset-0 h-full w-full"
            src={embedUrl}
            title={`${title} walkthrough`}
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      )}
    </>
  );
}

// A step the user already acted on, re-rendered faded (but still openable/rewatchable) with the
// outcome labelled below it. Used as a standalone conversation message in narrative mode.
export function SettledStepMessage({ step, status }: { step: SettledStepInfo; status: SettledStatus }) {
  return (
    <div className="flex items-start gap-2">
      <TruvAvatar />
      <div className="flex max-w-[85%] flex-col">
        <div className="rounded-2xl bg-grey20 px-4 py-2.5 text-grey90 opacity-60">
          <Typography className="text-sm font-medium">{step.title}</Typography>
          {step.description && (
            <Typography variant="body2" className="mt-0.5 text-grey70">
              {step.description}
            </Typography>
          )}
          <StepMedia title={step.title} link={step.link} videoUrl={step.videoUrl} />
        </div>
        <span
          className={clsx(
            'mt-1 pr-1 text-right text-xs',
            status === 'complete' ? 'font-medium text-success' : 'text-grey60',
          )}
        >
          {status === 'complete' ? 'Completed' : 'Skipped'}
        </span>
      </div>
    </div>
  );
}

type StepRowProps = {
  step: ChecklistStep;
  onComplete: () => Promise<unknown>;
  onSkip: () => Promise<unknown>;
  plain?: boolean;
};

// `plain` drops the bordered card + status icon so a step reads like an ordinary assistant message
// (used in narrative mode).
function StepRow({ step, onComplete, onSkip, plain = false }: StepRowProps) {
  const settled = step.status === 'complete' || step.status === 'skipped';
  // Which action is in flight, so its button shows a spinner. On success the row settles/unmounts,
  // clearing this; on failure we reset so it can be retried.
  const [busy, setBusy] = useState<'complete' | 'skipped' | null>(null);

  const run = (action: 'complete' | 'skipped', request: () => Promise<unknown>) => {
    setBusy(action);
    request().catch(() => setBusy(null));
  };
  const content = (
    <div className={clsx('min-w-0 flex-1', plain && 'animate-message-in')}>
      <Typography className={clsx('text-sm', settled ? 'text-grey60 line-through' : 'font-medium text-grey90')}>
        {step.title}
      </Typography>
      {!settled && step.description && (
        <Typography variant="body2" className="mt-0.5 text-grey70">
          {step.description}
        </Typography>
      )}
      {!settled && (
        <StepMedia
          title={step.title}
          link={step.link}
          videoUrl={step.video_url}
          extraActions={
            <>
              <Button
                size="small"
                variant="text"
                sx={{ fontSize: '0.75rem', minWidth: 76 }}
                disabled={busy !== null}
                onClick={() => run('complete', onComplete)}
              >
                {busy === 'complete' ? <CircularProgress size={12} color="inherit" /> : 'Mark done'}
              </Button>
              {step.skippable && (
                <Button
                  size="small"
                  variant="text"
                  sx={{ fontSize: '0.75rem', minWidth: 44, color: 'text.secondary' }}
                  disabled={busy !== null}
                  onClick={() => run('skipped', onSkip)}
                >
                  {busy === 'skipped' ? <CircularProgress size={12} color="inherit" /> : 'Skip'}
                </Button>
              )}
            </>
          }
        />
      )}
    </div>
  );
  if (plain) {
    return content;
  }
  return (
    <div className="rounded-xl border border-grey30 p-3">
      <div className="flex items-start gap-3">
        <StatusIcon status={step.status} />
        {content}
      </div>
    </div>
  );
}

// The server-driven checklist, rendered as an assistant message once the wizard is done. Steps
// auto-complete via sync (server) or can be marked done / skipped here (PATCH). In narrative mode,
// onStepSettled lets the parent record each in-session action as its own conversation message.
export function ConversationChecklist({ onStepSettled }: { onStepSettled?: OnStepSettled }) {
  const { query, updateStep } = useChecklist();
  const isNarrative = query.data?.display_mode === 'narrative';

  const body = (() => {
    if (query.isPending) {
      return <CircularProgress size={20} />;
    }
    if (query.isError || !query.data) {
      return <Typography className="text-sm text-grey70">Couldn't load your checklist. Try refreshing.</Typography>;
    }
    const { sections, completed_count, total_count, display_mode } = query.data;
    const progress = total_count ? Math.round((completed_count / total_count) * 100) : 0;
    const narrative = display_mode === 'narrative';
    // In narrative mode each in-session action is pushed to the conversation as its own message
    // (see onStepSettled), so the checklist bubble only ever shows the current step.
    const settleThen = (step: ChecklistStep, status: 'complete' | 'skipped') =>
      updateStep({ stepId: step.step_id, status }).then((result) => {
        if (narrative) {
          onStepSettled?.(
            { title: step.title, description: step.description, link: step.link, videoUrl: step.video_url },
            status,
          );
        }
        return result;
      });
    const rowHandlers = (step: ChecklistStep) => ({
      onComplete: () => settleThen(step, 'complete'),
      onSkip: () => settleThen(step, 'skipped'),
    });
    if (narrative) {
      const nextStep = sections.flatMap((section) => section.steps).find((step) => step.status === 'pending');
      // key on step_id so the row remounts (clearing its busy/spinner state) when the step advances.
      return nextStep ? (
        <StepRow key={nextStep.step_id} step={nextStep} {...rowHandlers(nextStep)} plain />
      ) : (
        <Typography variant="body2" className="text-grey70">
          You've completed every step. Nice work!
        </Typography>
      );
    }
    return (
      <>
        <div className="flex items-center justify-between">
          <Typography className="font-semibold text-grey90">Your setup checklist</Typography>
          <Typography variant="body2" className="text-grey60">
            {completed_count}/{total_count}
          </Typography>
        </div>
        <LinearProgress
          variant="determinate"
          value={progress}
          aria-label="Setup progress"
          aria-valuetext={`${completed_count} of ${total_count} complete`}
          className="mt-2 rounded"
        />
        <div className="mt-3 flex flex-col gap-4">
          {sections.map((section) => (
            <div key={section.section_id}>
              <Typography variant="body2" className="mb-1 font-semibold text-grey70">
                {section.section_label}
              </Typography>
              <div className="flex flex-col gap-2">
                {section.steps.map((step) => (
                  <StepRow key={step.step_id} step={step} {...rowHandlers(step)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  })();

  return (
    <div className="flex items-start gap-2">
      <TruvAvatar />
      <div
        className={clsx(
          'min-w-0 rounded-2xl',
          isNarrative ? 'max-w-[85%] bg-grey20 px-4 py-2.5' : 'flex-1 border border-grey30 bg-white p-4',
        )}
      >
        {body}
      </div>
    </div>
  );
}
